// Mock Team Data - Simulates collaborative environment

import type {
  TeamMember,
  FrameworkStory,
  TeamResource,
  TeamActivity,
  Comment,
  Discussion,
  TeamInsight,
} from '../types/collaboration';

// Mock Team Members
export const mockTeamMembers: TeamMember[] = [
  {
    id: 'user-1',
    name: 'Sarah Chen',
    email: 'sarah.chen@company.com',
    role: 'leader',
    joinedAt: '2024-01-15T00:00:00Z',
    frameworksExplored: 24,
    outcomesContributed: 8,
    lastActive: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
  },
  {
    id: 'user-2',
    name: 'Marcus Thompson',
    email: 'marcus.t@company.com',
    role: 'member',
    joinedAt: '2024-01-20T00:00:00Z',
    frameworksExplored: 18,
    outcomesContributed: 5,
    lastActive: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(), // 5 hours ago
  },
  {
    id: 'user-3',
    name: 'Elena Rodriguez',
    email: 'elena.r@company.com',
    role: 'member',
    joinedAt: '2024-02-01T00:00:00Z',
    frameworksExplored: 15,
    outcomesContributed: 7,
    lastActive: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
  },
  {
    id: 'user-4',
    name: 'James Park',
    email: 'james.park@company.com',
    role: 'contributor',
    joinedAt: '2024-02-10T00:00:00Z',
    frameworksExplored: 12,
    outcomesContributed: 3,
    lastActive: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago
  },
  {
    id: 'user-5',
    name: 'Aisha Patel',
    email: 'aisha.p@company.com',
    role: 'member',
    joinedAt: '2024-02-15T00:00:00Z',
    frameworksExplored: 20,
    outcomesContributed: 6,
    lastActive: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(), // 6 hours ago
  },
];

// Mock Framework Stories
export const mockFrameworkStories: FrameworkStory[] = [
  {
    id: 'story-1',
    frameworkId: 'pf.degap.stages',
    frameworkName: 'DEGAP (5 Stages)',
    author: mockTeamMembers[0],
    title: 'How DEGAP Transformed Our Q1 Planning',
    description: 'Using DEGAP to prioritize 47 feature requests into a coherent roadmap',
    situation: 'Our product team was overwhelmed with 47 feature requests from customers, sales, and internal stakeholders. Traditional prioritization led to conflicts and unclear direction.',
    application: 'We used DEGAP to structure our approach: Decided to focus on revenue impact, Engaged key stakeholders early, Gathered data on customer pain points, Arranged features by strategic themes, and Prioritized based on multi-factor scoring.',
    outcome: 'Reduced to 12 high-impact features for Q1. Stakeholder alignment improved 80%. Team velocity increased as everyone understood the "why" behind priorities.',
    lessonsLearned: [
      'Front-load stakeholder engagement - saves time later',
      'Data gathering phase is crucial - don\'t skip it',
      'Visual arrangement helps spot patterns we missed in spreadsheets',
    ],
    tags: ['roadmap', 'prioritization', 'stakeholder-management'],
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    likes: 12,
    commentCount: 5,
    featured: true,
  },
  {
    id: 'story-2',
    frameworkId: 'gf.relevance.framework',
    frameworkName: 'Relevance Framework',
    author: mockTeamMembers[2],
    title: 'Improving Team Communication with "What, Why, How"',
    description: 'Applied Relevance Framework to reduce miscommunication in cross-functional projects',
    situation: 'Cross-functional initiatives kept stalling because different teams had different understandings of project goals and methods.',
    application: 'Mandated all project kickoffs to answer: What are we building? Why does it matter? How will we do it? Created templates for each question.',
    outcome: 'Project kickoff meetings reduced from 2 hours to 45 minutes. Alignment scores in retrospectives improved from 6.2 to 8.7/10. Fewer mid-project pivots.',
    lessonsLearned: [
      'The "Why" question uncovers hidden assumptions',
      'Visual answers work better than text-heavy docs',
      'Revisit these questions at mid-project checkpoints',
    ],
    tags: ['communication', 'alignment', 'cross-functional'],
    createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    likes: 8,
    commentCount: 3,
    featured: false,
  },
  {
    id: 'story-3',
    frameworkId: 'pf.framework.diagnostic.role.types',
    frameworkName: 'Role Types',
    author: mockTeamMembers[1],
    title: 'Clarifying Roles in Our New Initiative',
    description: 'Using Role Types framework to define clear responsibilities',
    situation: 'New strategic initiative had 8 people involved but unclear who did what. Lots of "I thought you were handling that" moments.',
    application: 'Used Role Types framework to map: Decision makers, Implementers, Advisors, and Informed parties. Created RACI matrix based on framework.',
    outcome: 'Eliminated 60% of status meetings. Decisions happened 2x faster. Team satisfaction increased from 6.5 to 8.2/10.',
    lessonsLearned: [
      'Distinguish "consulted" from "informed" - huge difference',
      'One decision maker per domain - shared ownership = no ownership',
      'Document and share role clarity - don\'t assume people remember',
    ],
    tags: ['roles', 'decision-making', 'team-dynamics'],
    createdAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
    likes: 15,
    commentCount: 7,
    featured: true,
  },
];

// Mock Team Resources
export const mockTeamResources: TeamResource[] = [
  {
    id: 'resource-1',
    title: 'DEGAP Workshop Template',
    type: 'template',
    description: 'Ready-to-use Miro board template for facilitating DEGAP prioritization sessions',
    frameworkIds: ['pf.degap.stages'],
    author: mockTeamMembers[0],
    createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    downloads: 23,
  },
  {
    id: 'resource-2',
    title: 'Team Prioritization Playbook',
    type: 'playbook',
    description: 'Complete guide to running effective prioritization sessions using Peak Priorities frameworks',
    frameworkIds: ['pf.degap.stages', 'gf.diagram.levels', 'cont.multires.levels'],
    author: mockTeamMembers[2],
    createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    downloads: 45,
  },
  {
    id: 'resource-3',
    title: 'Stakeholder Mapping Quick Guide',
    type: 'guide',
    description: '1-page reference for identifying and engaging stakeholders',
    frameworkIds: ['pf.framework.diagnostic.role.types'],
    author: mockTeamMembers[1],
    createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    downloads: 18,
  },
];

// Mock Comments
export const mockComments: Comment[] = [
  {
    id: 'comment-1',
    outcomeId: 'story-1',
    author: mockTeamMembers[1],
    content: 'This is brilliant! We had the same challenge last quarter. Did you find the "Gather" phase took longer than expected?',
    createdAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    reactions: [
      { emoji: '👍', userId: 'user-3', userName: 'Elena Rodriguez' },
      { emoji: '💡', userId: 'user-4', userName: 'James Park' },
    ],
  },
  {
    id: 'comment-2',
    outcomeId: 'story-1',
    author: mockTeamMembers[0],
    content: 'Yes! Gathering took 1.5 weeks vs our planned 1 week. But it paid off - much better decisions.',
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    replyToId: 'comment-1',
    reactions: [],
  },
  {
    id: 'comment-3',
    outcomeId: 'story-3',
    author: mockTeamMembers[4],
    content: 'We should standardize this across all strategic initiatives. Can we add this to our project kickoff checklist?',
    createdAt: new Date(Date.now() - 19 * 24 * 60 * 60 * 1000).toISOString(),
    reactions: [
      { emoji: '✅', userId: 'user-1', userName: 'Sarah Chen' },
      { emoji: '👍', userId: 'user-2', userName: 'Marcus Thompson' },
    ],
  },
];

// Mock Discussions
export const mockDiscussions: Discussion[] = [
  {
    id: 'discussion-1',
    title: 'Best practices for remote DEGAP workshops?',
    frameworkId: 'pf.degap.stages',
    author: mockTeamMembers[3],
    createdAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    lastActivityAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    commentCount: 8,
    participants: [mockTeamMembers[0], mockTeamMembers[1], mockTeamMembers[2]],
    tags: ['remote-work', 'facilitation'],
    resolved: false,
  },
  {
    id: 'discussion-2',
    title: 'How to handle conflicting stakeholder priorities?',
    author: mockTeamMembers[4],
    createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    lastActivityAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    commentCount: 12,
    participants: [mockTeamMembers[0], mockTeamMembers[1], mockTeamMembers[3]],
    tags: ['stakeholder-management', 'decision-making'],
    resolved: true,
  },
];

// Mock Team Activity
export const mockTeamActivity: TeamActivity[] = [
  {
    id: 'activity-1',
    type: 'story-published',
    actor: mockTeamMembers[0],
    timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    title: 'Published framework story',
    description: 'How DEGAP Transformed Our Q1 Planning',
    relatedId: 'story-1',
    relatedUrl: '/team/stories/story-1',
  },
  {
    id: 'activity-2',
    type: 'resource-added',
    actor: mockTeamMembers[2],
    timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    title: 'Added team resource',
    description: 'Team Prioritization Playbook',
    relatedId: 'resource-2',
  },
  {
    id: 'activity-3',
    type: 'comment-posted',
    actor: mockTeamMembers[1],
    timestamp: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    title: 'Commented on story',
    description: 'How DEGAP Transformed Our Q1 Planning',
    relatedId: 'story-1',
  },
  {
    id: 'activity-4',
    type: 'achievement-unlocked',
    actor: mockTeamMembers[4],
    timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    title: 'Unlocked achievement',
    description: 'Framework Master - DEGAP',
  },
  {
    id: 'activity-5',
    type: 'story-published',
    actor: mockTeamMembers[1],
    timestamp: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
    title: 'Published framework story',
    description: 'Clarifying Roles in Our New Initiative',
    relatedId: 'story-3',
  },
];

// Mock Team Insights
export const mockTeamInsights: TeamInsight[] = [
  {
    type: 'trending-framework',
    title: 'Trending Framework',
    description: 'DEGAP is your team\'s most-applied framework this month',
    metric: 8,
    relatedId: 'pf.degap.stages',
    relatedUrl: '/entry/pf.degap.stages',
    timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    type: 'top-contributor',
    title: 'Top Contributor',
    description: 'Sarah Chen shared 3 new framework stories this month',
    metric: 3,
    timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    type: 'milestone',
    title: 'Team Milestone',
    description: 'Your team has explored 40+ frameworks!',
    metric: 42,
    timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    type: 'new-story',
    title: 'New Success Story',
    description: 'Elena Rodriguez shared how Relevance Framework improved team communication',
    relatedId: 'story-2',
    timestamp: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

// Team Stats
export const mockTeamStats = {
  totalMembers: mockTeamMembers.length,
  activeThisWeek: 3,
  totalOutcomes: 28,
  sharedOutcomes: 15,
  totalStories: mockFrameworkStories.length,
  totalResources: mockTeamResources.length,
  frameworksExplored: 42,
  totalComments: 47,
};

